<template>
  <Card class="w-full">
    <template #title>
      <div class="flex items-center justify-between">
        <span class="flex items-center gap-2">
          <i class="pi pi-facebook" />
          <span>حسابات فيسبوك</span>
          <Tag v-if="rows && rows.length" :value="rows.length" severity="info" />
        </span>
        <div class="flex gap-2" v-if="!readonly">
          <Button size="small" icon="pi pi-plus" label="إضافة" @click="addRow" outlined />
          <Button size="small" icon="pi pi-save" label="حفظ" :disabled="!hasChanges || saving" @click="saveAll" />
        </div>
      </div>
    </template>

    <template #content>
      <DataTable
        ref="dt"
        v-model:editingRows="editingRows"
        :value="rows"
        :loading="loading"
        dataKey="_key"
        editMode="row"
        responsiveLayout="scroll"
        class="text-sm"
        @row-edit-init="onRowEditInit"
        @row-edit-save="onRowEditSave"
        @row-edit-cancel="onRowEditCancel"
      >
        <Column field="profile_url" header="الرابط" :style="{minWidth: '22rem'}" :editable="!readonly">
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <i class="pi pi-link text-color-secondary"></i>
              <a v-if="data.profile_url" :href="normalizedUrl(data.profile_url)" target="_blank" rel="noopener" class="text-primary hover:underline">
                {{ normalizedUrl(data.profile_url) }}
              </a>
              <span v-else class="text-color-secondary italic">—</span>
              <Button v-if="data.profile_url" icon="pi pi-external-link" 
                text rounded size="small" :aria-label="'Open'" :as="'a'" 
                :href="normalizedUrl(data.profile_url)" target="_blank" />
            </div>
          </template>
          <template #editor="{ data }">
            <InputText v-model.trim="data.profile_url" placeholder="https://facebook.com/..." class="w-full" />
            <small v-if="errors[data._key]?.profile_url" class="p-error">{{ errors[data._key].profile_url }}</small>
          </template>
        </Column>

        <Column field="username" header="اسم المستخدم" :style="{minWidth: '16rem'}" :editable="!readonly">
          <template #body="{ data }">
            <div class="flex items-center gap-1">
              <span class="text-color-secondary">facebook.com/</span>
              <span class="font-medium">{{ data.username || '—' }}</span>
            </div>
          </template>
          <template #editor="{ data }">
            <InputGroup>
              <InputGroupAddon>facebook.com/</InputGroupAddon>
              <InputText v-model.trim="data.username" placeholder="john.smith" />
            </InputGroup>
            <small v-if="errors[data._key]?.username" class="p-error">{{ errors[data._key].username }}</small>
          </template>
        </Column>

        <Column field="fb_id" header="FB ID" :style="{minWidth: '10rem'}" :editable="!readonly">
          <template #editor="{ data }">
            <InputNumber v-model="data.fb_id" :useGrouping="false" inputId="fbid" class="w-full" />
            <small v-if="errors[data._key]?.fb_id" class="p-error">{{ errors[data._key].fb_id }}</small>
          </template>
          <template #body="{ data }">{{ data.fb_id || '—' }}</template>
        </Column>

        <Column field="profile_title" header="عنوان الملف" :editable="!readonly">
          <template #editor="{ data }">
            <InputText v-model.trim="data.profile_title" placeholder="عنوان الملف" class="w-full" />
          </template>
        </Column>
        <Column field="company_name" header="الشركة" :editable="!readonly">
          <template #editor="{ data }">
            <InputText v-model.trim="data.company_name" placeholder="اسم الشركة" class="w-full" />
          </template>
        </Column>
        <Column field="job_title" header="المسمى الوظيفي" :editable="!readonly">
          <template #editor="{ data }">
            <InputText v-model.trim="data.job_title" placeholder="المسمى الوظيفي" class="w-full" />
          </template>
        </Column>

        <!-- Replace your current إجراءات column with this: -->
        <Column
          header="إجراءات"
          :rowEditor="!readonly"
          bodyStyle="text-align:center"
          :style="{ width: '8rem' }"
        />

          <!-- <template #body="{ data }">
            <div class="flex justify-center gap-1" v-if="!readonly">
              <Button icon="pi pi-pencil" title="تعديل" text rounded @click="editingRows[data._key] = true" />
              <Button icon="pi pi-trash" title="حذف" text rounded severity="danger" @click="confirmDelete($event, data)" />
            </div>
          </template> -->
        
        <Column header="حذف" :style="{ width: '6rem', textAlign: 'center' }">
          <template #body="{ data }">
            <Button
              v-if="!readonly"
              icon="pi pi-trash"
              title="حذف"
              text
              rounded
              severity="danger"
              @click="confirmDelete($event, data)"
            />
          </template>
        </Column>


        <template #empty>
          <div class="py-6 text-center text-color-secondary">لا توجد حسابات بعد</div>
        </template>
      </DataTable>
    </template>

    <ConfirmPopup />
  </Card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import Card from 'primevue/card'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import InputGroup from 'primevue/inputgroup'
import InputGroupAddon from 'primevue/inputgroupaddon'
import Tag from 'primevue/tag'
import ConfirmPopup from 'primevue/confirmpopup'
import axios from 'axios'

const props = defineProps<{ employeeId: number | string, readonly?: boolean }>()

const toast = useToast()
const confirm = useConfirm()
const dt = ref<any>(null)

const loading = ref(false)
const saving = ref(false)
const rows = ref<any[]>([])
const originalKeys = ref(new Set<string>()) // to track existing items
const errors = ref<Record<string, any>>({})
const editingRows = ref<any[]>([])


const hasChanges = computed(() => rows.value.some(r => r._deleted || !r.profile_id || r._dirty))

const normalizedUrl = (u?: string) => {
  if (!u) return ''
  if (/^https?:\/\//i.test(u)) return u
  return `https://facebook.com/${u.replace(/^\/+/, '')}`
}

function markDirty(r: any) { r._dirty = true }

async function addRow() {
  const newRow = {
    _key: `tmp-${Math.random().toString(36).slice(2)}`,
    employee_id: Number(props.employeeId) || 0,
    profile_url: '', username: '', fb_id: undefined,
    profile_title: '', company_name: '', job_title: '', address_1: '', address_2: '',
    _dirty: true, _deleted: false
  }
  rows.value.push(newRow)
  await nextTick()
  if (!editingRows.value.includes(newRow)) {
  editingRows.value = [...editingRows.value, newRow]
}
}

function onRowEditInit(e: { data: any }) {
  if (!editingRows.value.includes(e.data)) {
    editingRows.value = [...editingRows.value, e.data]
  }
}

function onRowEditSave(e: { data: any; newData: any; index: number }) {
 
  rows.value[e.index] = { ...e.newData, _dirty: true }
  rows.value = [...rows.value]
  editingRows.value = editingRows.value.filter(r => r !== e.data)
}

function onRowEditCancel(e: { data: any }) {
  editingRows.value = editingRows.value.filter(r => r !== e.data)
}


function confirmDelete(event: any, r: any) {
  confirm.require({
    target: event.currentTarget,
    message: 'هل تريد حذف الحساب؟',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'حذف',
    rejectLabel: 'إلغاء',
    accept: () => { r._deleted = true; markDirty(r); saveAll() }
  })
}

function validateUnique() {
  const errs: Record<string, any> = {}
  const seenUrl = new Map<string, string>()
  const seenUser = new Map<string, string>()
  const seenId = new Map<string, string>()

  for (const r of rows.value.filter(r => !r._deleted)) {
    const k = r._key
    errs[k] = {}
    if (r.profile_url) {
      const u = normalizedUrl(r.profile_url).toLowerCase()
      if (seenUrl.has(u)) errs[k].profile_url = 'الرابط مكرر'
      else seenUrl.set(u, k)
    }
    if (r.username) {
      const un = r.username.toLowerCase()
      if (seenUser.has(un)) errs[k].username = 'اسم المستخدم مكرر'
      else seenUser.set(un, k)
    }
    if (r.fb_id) {
      const id = String(r.fb_id)
      if (seenId.has(id)) errs[k].fb_id = 'معرف FB مكرر'
      else seenId.set(id, k)
    }
  }
  errors.value = errs
  return Object.values(errs).every(o => !o.profile_url && !o.username && !o.fb_id)
}

function payloadFromRow(r: any) {
  const eid = Number(props.employeeId)
  return {
    employee_id: eid,
    profile_url: r.profile_url || null,
    username: r.username || null,
    fb_id: r.fb_id || null,
    profile_title: r.profile_title || null,
    company_name: r.company_name || null,
    job_title: r.job_title || null,
    address_1: r.address_1 || null,
    address_2: r.address_2 || null,
  }
}

async function saveAll() {
  if (!validateUnique()) {
    toast.add({ severity: 'warn', summary: 'تكرارات', detail: 'تأكد من عدم تكرار الرابط/الاسم/المعرف', life: 3500 })
    return
  }

  saving.value = true
  const eid = Number(props.employeeId)
  try {
    // deletes first
    for (const r of rows.value.filter(r => r.profile_id && r._deleted)) {
      await axios.delete(`/api/employees/${eid}/facebook-profiles/${r.profile_id}`)
    }
    // upserts
    for (const r of rows.value.filter(r => !r._deleted)) {
      const payload = payloadFromRow(r)
      if (r.profile_id) {
        if (r._dirty) {
          const { data } = await axios.patch(`/api/employees/${eid}/facebook-profiles/${r.profile_id}`, payload)
          Object.assign(r, data)
        }
      } else {
        const { data } = await axios.post(`/api/employees/${eid}/facebook-profiles`, payload)
        Object.assign(r, data)
      }
      r._dirty = false
    }

    // normalize keys after create/delete
    rows.value = rows.value
      .filter(r => !r._deleted)
      .map(r => ({ ...r, _key: r.profile_id ? `p-${r.profile_id}` : r._key }))
    editingRows.value = []
    toast.add({ severity: 'success', summary: 'تم الحفظ', life: 2000 })
  } catch (e: any) {
    const status = e?.response?.status
    const detail = e?.response?.data?.detail
    toast.add({ severity: 'error', summary: 'فشل الحفظ', detail: detail || `HTTP ${status || ''}` })
    console.error('FB save failed', e?.response || e)
  } finally {
    saving.value = false
  }
}

async function loadAll() {
  loading.value = true
  try {
    const { data } = await axios.get(`/api/employees/${props.employeeId}/facebook-profiles`)
    rows.value = (data || []).map((d: any) => ({ ...d, _key: `p-${d.profile_id}`, _dirty: false, _deleted: false }))
  } finally {
    loading.value = false
  }
}

onMounted(loadAll)
</script>
